/**
 * When switching flashcard types, map field values in target type order.
 * 1) Match by key + fieldType (handles identical keys across types).
 * 2) Fallback: next unused source field with same role + fieldType.
 * 3) Cross-type: image <-> imagepos (extract/embed base64 from JSON).
 */

import { parseImageFieldValue } from '../../../common/imageFieldUtils';

const extractImageFromValue = (fieldType, value) => {
  if (fieldType === 'image') {
    const imgs = parseImageFieldValue(value);
    return imgs[0] || '';
  }
  if (fieldType === 'imagepos') {
    try {
      return JSON.parse(value || '{}').image || '';
    } catch {
      return '';
    }
  }
  return '';
};

const wrapImageForTarget = (targetFieldType, base64) => {
  if (!base64) return '';
  if (targetFieldType === 'image') return base64;
  if (targetFieldType === 'imagepos') {
    return JSON.stringify({ x: 0, y: 0, radius: 20, image: base64 });
  }
  return '';
};

const IMAGE_TYPES = new Set(['image', 'imagepos']);

export const enrichFieldsWithType = (typeObj, cardFields) => {
  if (!typeObj?.fields?.length) return [];
  return typeObj.fields.map((def, i) => ({
    key: def.key,
    fieldType: def.fieldType,
    role: def.role,
    value: cardFields[i]?.value ?? '',
  }));
};

/**
 * @param {object} targetTypeObj flashcard type with fields[]
 * @param {Array<{key, fieldType, role, value}>} enrichedSource values from previous type or snapshot
 */
export const mapFieldsForTargetType = (targetTypeObj, enrichedSource) => {
  const fieldsDef = targetTypeObj?.fields || [];
  if (!fieldsDef.length) return [];

  const source = Array.isArray(enrichedSource) ? enrichedSource : [];
  const used = new Array(source.length).fill(false);

  const takeExact = (def) => {
    const idx = source.findIndex(
      (e, i) =>
        !used[i] &&
        e.key === def.key &&
        e.fieldType === def.fieldType,
    );
    if (idx < 0) return undefined;
    used[idx] = true;
    return source[idx].value ?? '';
  };

  const takeRoleFieldType = (def) => {
    const idx = source.findIndex(
      (e, i) =>
        !used[i] &&
        e.fieldType === def.fieldType &&
        e.role === def.role,
    );
    if (idx < 0) return undefined;
    used[idx] = true;
    return source[idx].value ?? '';
  };

  const takeCrossImage = (def) => {
    if (!IMAGE_TYPES.has(def.fieldType)) return undefined;
    const idx = source.findIndex(
      (e, i) => !used[i] && IMAGE_TYPES.has(e.fieldType) && e.fieldType !== def.fieldType,
    );
    if (idx < 0) return undefined;
    const base64 = extractImageFromValue(source[idx].fieldType, source[idx].value);
    if (!base64) return undefined;
    used[idx] = true;
    return wrapImageForTarget(def.fieldType, base64);
  };

  return fieldsDef.map((def) => {
    const exact = takeExact(def);
    if (exact !== undefined) {
      return { key: def.key, value: exact };
    }
    const fallback = takeRoleFieldType(def);
    if (fallback !== undefined) {
      return { key: def.key, value: fallback };
    }
    const cross = takeCrossImage(def);
    if (cross !== undefined) {
      return { key: def.key, value: cross };
    }
    return { key: def.key, value: def.defaultValue || '' };
  });
};

export const deriveHeaderAndSolutionsPatch = (
  typeFields,
  newFields,
  prevSolutions,
) => {
  const getValAt = (idx) => newFields[idx]?.value ?? '';

  const headerPatch = {};
  const firstQIdx = typeFields.findIndex(
    (f) => f.role === 'question' && f.fieldType === 'text',
  );
  if (firstQIdx >= 0) {
    headerPatch.title = getValAt(firstQIdx);
  }

  const base = prevSolutions?.[0] || {
    text: '',
    sound: '',
    infotext: '',
    correct: false,
  };
  const solutions = [{ ...base }];

  const firstSIdx = typeFields.findIndex(
    (f) =>
      f.role === 'solution' &&
      (f.fieldType === 'text' || f.fieldType === 'code'),
  );
  if (firstSIdx >= 0) {
    solutions[0] = { ...solutions[0], text: getValAt(firstSIdx) };
  }

  const firstSoundIdx = typeFields.findIndex(
    (f) => f.role === 'solution' && f.fieldType === 'sound',
  );
  if (firstSoundIdx >= 0) {
    const sound = getValAt(firstSoundIdx);
    solutions[0] = { ...solutions[0], sound };
    headerPatch.soundavailable = !!sound;
  }

  return { headerPatch, solutions };
};
